import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import {
  Crown,
  EyeOff,
  CheckCircle2,
  ArrowRight,
  Loader2,
  Building2,
  Target,
  FileText,
} from "lucide-react";
import { AuthDialog } from "@/components/site/AuthDialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { rememberVipInvitation } from "@/lib/vip-invitation-intent";
import { useIsVip } from "@/hooks/use-user-roles";

export const Route = createFileRoute("/invitacion-vip")({
  validateSearch: z.object({ inicio: z.boolean().optional().catch(undefined) }),
  head: () => ({
    meta: [
      { title: "Bienvenido al área VIP de Bizaclia" },
      {
        name: "description",
        content:
          "Publica y recibe activos off-market. Activa tu acceso al área VIP de Bizaclia y elige cómo empezar.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { name: "referrer", content: "no-referrer" },
    ],
  }),
  component: VipInvitationPage,
});

function VipInvitationPage() {
  const { inicio } = Route.useSearch();
  const { vipActivo, loading: vipLoading } = useIsVip();
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    let authChanged = false;
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      authChanged = true;
      if (active) {
        setEmail(session?.user.email ?? null);
        setLoading(false);
      }
    });
    void supabase.auth
      .getSession()
      .then(({ data }) => {
        if (active && !authChanged) {
          setEmail(data.session?.user.email ?? null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  return (
    <main className="bg-[#0B1120] text-white">
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <section>
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-2 text-sm font-medium text-amber-300">
              <Crown className="h-4 w-4" /> Una nueva forma de encontrar oportunidades
            </span>
            <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
              Bienvenido al área VIP de Bizaclia
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-300">
              Hay oportunidades que no aparecen en los portales. Aquí puedes compartir tus activos{" "}
              <strong className="text-white">off-market</strong> y encontrar los que encajan con lo
              que buscas.
            </p>
            <p className="mt-4 leading-relaxed text-slate-300">
              Un espacio para propietarios, profesionales e inversores que quieren gestionar sus
              operaciones fuera del escaparate público.
            </p>
            <div className="mt-7 flex flex-wrap gap-x-5 gap-y-3 text-sm text-amber-200">
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Acceso con esta invitación
              </span>
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Publicación gratuita
              </span>
            </div>
          </section>
          <section
            id="acceso-vip"
            className="scroll-mt-24 rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-xl sm:p-8"
            aria-live="polite"
          >
            {loading || (inicio && vipLoading) ? (
              <div className="flex items-center justify-center gap-2 py-10">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span>Comprobando tu acceso…</span>
              </div>
            ) : inicio && email && vipActivo ? (
              <>
                <div className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-primary">
                  <CheckCircle2 className="h-5 w-5" /> Tu acceso VIP está activo
                </div>
                <h2 className="text-2xl font-bold">¿Cómo te gustaría comenzar?</h2>
                <p className="mt-3 text-sm text-muted-foreground">
                  Elige tu primer paso. Puedes utilizar ambas opciones cuando quieras.
                </p>
                <div className="mt-6 grid gap-3">
                  <Link
                    to="/mybizaclia"
                    search={{ tab: "vip", wizard: true }}
                    className="group rounded-xl border p-5 transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                  >
                    <Building2 className="mb-3 h-6 w-6 text-primary" />
                    <h3 className="flex items-center justify-between gap-2 font-semibold">
                      Publicar activos off-market <ArrowRight className="h-4 w-4 shrink-0" />
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Presenta tu activo, añade sus datos económicos y envíalo a revisión para el
                      área VIP.
                    </p>
                  </Link>
                  <Link
                    to="/conector-vip"
                    className="group rounded-xl border p-5 transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                  >
                    <Target className="mb-3 h-6 w-6 text-primary" />
                    <h3 className="flex items-center justify-between gap-2 font-semibold">
                      Recibir activos off-market <ArrowRight className="h-4 w-4 shrink-0" />
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Cuéntanos qué buscas y configura tus criterios en el Conector VIP.
                    </p>
                  </Link>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold">Tu acceso al área VIP empieza aquí</h2>
                <p className="mb-6 mt-3 text-sm leading-relaxed text-muted-foreground">
                  Esta invitación activará tu acceso VIP automáticamente si aún no lo tienes.
                </p>
                {email ? (
                  <>
                    <p className="mb-4 break-all text-sm text-muted-foreground">
                      Continuar como <strong className="text-foreground">{email}</strong>
                    </p>
                    <Button
                      className="h-auto min-h-11 w-full whitespace-normal"
                      onClick={() => rememberVipInvitation(email)}
                    >
                      Comprobar mi acceso VIP <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
                    </Button>
                  </>
                ) : (
                  <AuthDialog
                    inline
                    emailTitle="Tu correo electrónico"
                    emailSubmitLabel="Comprobar mi acceso VIP"
                    onEmailContinue={rememberVipInvitation}
                  />
                )}
                <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
                  Después podrás elegir entre publicar tus activos o recibir oportunidades
                  off-market.
                </p>
              </>
            )}
          </section>
        </div>
        <section className="mt-20 sm:mt-24" aria-labelledby="vip-benefits">
          <p className="text-sm font-medium uppercase tracking-widest text-amber-300">
            Más allá del portal público
          </p>
          <h2 id="vip-benefits" className="mt-3 text-3xl font-semibold tracking-tight">
            Un espacio para tus operaciones off-market
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {[
              {
                icon: EyeOff,
                title: "Fuera del escaparate público",
                text: "Los activos off-market se muestran en el área VIP, separados de los anuncios públicos del portal.",
              },
              {
                icon: Target,
                title: "Búsquedas con tus criterios",
                text: "Indica la ubicación, el tipo de activo y la operación que te interesan para recibir avisos de coincidencias.",
              },
              {
                icon: FileText,
                title: "Información para valorar",
                text: "Comparte precios, facturación, rentabilidad y otros datos económicos relevantes para entender cada operación.",
              },
            ].map(({ icon: Icon, title, text }) => (
              <article key={title} className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <Icon className="h-6 w-6 text-amber-300" />
                <h3 className="mt-5 text-lg font-semibold">{title}</h3>
                <p className="mt-3 leading-relaxed text-slate-300">{text}</p>
              </article>
            ))}
          </div>
        </section>
        <section className="mt-16 grid gap-8 border-t border-white/10 pt-12 sm:grid-cols-2">
          <div>
            <h2 className="text-2xl font-semibold">¿Tienes un activo que quieres compartir?</h2>
            <p className="mt-4 leading-relaxed text-slate-300">
              Publícalo desde la pestaña Off-market de My Bizaclia. El asistente te acompañará con
              la descripción, ubicación, fotos y datos económicos. Nuestro equipo revisará el
              anuncio antes de que aparezca en el área VIP.
            </p>
          </div>
          <div>
            <h2 className="text-2xl font-semibold">¿Buscas tu próxima oportunidad?</h2>
            <p className="mt-4 leading-relaxed text-slate-300">
              Completa el Bizaclia Match VIP con lo que necesitas. Tus preferencias ayudarán a identificar
              activos que encajen con tu búsqueda. También puedes publicar tus propios activos desde
              la misma cuenta.
            </p>
          </div>
        </section>
        <div className="mt-14 rounded-2xl border border-amber-300/20 bg-amber-300/5 p-8 text-center">
          <h2 className="text-2xl font-semibold">Tu próximo paso empieza en Bizaclia VIP</h2>
          <p className="mt-3 text-slate-300">Activa tu acceso y elige cómo te gustaría comenzar.</p>
          <Button asChild className="mt-6">
            <a href="#acceso-vip">
              Comprobar mi acceso VIP <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>
    </main>
  );
}
